"use client";

/**
 * Scene ILLUSTRATIONS for the app's "soul" surfaces: the analysis wait
 * screen, 404, trial-ended, the install page, and the empty personal
 * map. Bigger siblings of ToolAnimations' 48x48 vignettes - same
 * craft rules:
 *
 * - transform/opacity/stroke-dashoffset-only motion, pure CSS keyframes
 * - transparent background (theme-safe), explicit hexes (the dark-mode
 *   palette remap can't dim them)
 * - reduced-motion resting frame = the final readable state
 * - memo() wrapped; every class is namespaced per scene
 *
 * ScanAnim deliberately has NO fade-out seam: it plays while the user
 * WAITS, and a dip in motion reads as "it hung".
 */
import { memo, type SVGProps } from "react";
import { useScenePause } from "./useScenePause";

const INDIGO = "#6366f1";
const INDIGO_D = "#4f46e5";
const INDIGO_L = "#a5b4fc";
const EMERALD = "#10b981";
const AMBER = "#f59e0b";
const ROSE = "#f43f5e";
const GRAY = "#9ca3af";
const GRAY_L = "#e5e7eb";

/* ── ScanAnim - the analyze/compare wait screen ─────────────────────
   A listing card gets read: photo tiles slot in left→right, a scan
   beam sweeps the card, fact lines draw themselves, and the verdict
   gauge needle warms up right of it. 4.8s seamless loop. */
function ScanAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg ref={sceneRef} viewBox="0 0 132 64" className="scene-svg h-full w-full" aria-hidden>
      {/* card frame */}
      <rect x="6" y="8" width="76" height="48" rx="5" fill="none" stroke={GRAY_L} strokeWidth="1.6" />
      {/* photo tiles */}
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          className={`scn-tile scn-tile-${i}`}
          x={12 + i * 22}
          y={14}
          width="18"
          height="14"
          rx="2.5"
          fill={INDIGO}
          fillOpacity="0.85"
        />
      ))}
      {/* fact lines - draw themselves under the photos */}
      <line className="scn-line scn-line-0" x1="12" y1="36" x2="70" y2="36" stroke={GRAY} strokeWidth="2.4" strokeLinecap="round" pathLength={1} />
      <line className="scn-line scn-line-1" x1="12" y1="43" x2="58" y2="43" stroke={GRAY_L} strokeWidth="2.4" strokeLinecap="round" pathLength={1} />
      <line className="scn-line scn-line-2" x1="12" y1="50" x2="64" y2="50" stroke={GRAY_L} strokeWidth="2.4" strokeLinecap="round" pathLength={1} />
      {/* scan beam sweeping the card */}
      <rect className="scn-beam" x="6" y="8" width="10" height="48" rx="3" fill={INDIGO} fillOpacity="0.16" />
      <line className="scn-beam" x1="11" y1="10" x2="11" y2="54" stroke={INDIGO} strokeWidth="1.4" strokeLinecap="round" strokeOpacity="0.8" />
      {/* verdict gauge, warming up */}
      <g>
        <path d="M92 50 A 20 20 0 0 1 128 50" fill="none" stroke={GRAY_L} strokeWidth="4" strokeLinecap="round" />
        <path className="scn-arc" d="M92 50 A 20 20 0 0 1 128 50" fill="none" stroke={EMERALD} strokeWidth="4" strokeLinecap="round" pathLength={1} />
        <g className="scn-needle">
          <line x1="110" y1="50" x2="110" y2="35" stroke={INDIGO_D} strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="110" cy="50" r="2.6" fill={INDIGO_D} />
        </g>
      </g>
      <style>{`
        @keyframes scnTile {
          0%, 100% { opacity: 0; transform: translateY(3px); }
          8%, 85%  { opacity: 1; transform: translateY(0); }
        }
        @keyframes scnLine {
          0%       { stroke-dashoffset: 1; }
          40%, 88% { stroke-dashoffset: 0; }
          100%     { stroke-dashoffset: 1; }
        }
        @keyframes scnBeam {
          0%   { transform: translateX(0); opacity: 0; }
          6%   { opacity: 1; }
          46%  { transform: translateX(66px); opacity: 1; }
          52%  { opacity: 0; }
          100% { transform: translateX(0); opacity: 0; }
        }
        @keyframes scnNeedle {
          0%, 10% { transform: rotate(-72deg); }
          45%     { transform: rotate(38deg); }
          62%     { transform: rotate(22deg); }
          85%     { transform: rotate(30deg); }
          100%    { transform: rotate(-72deg); }
        }
        @keyframes scnArc {
          0%, 8%   { stroke-dashoffset: 1; }
          50%, 85% { stroke-dashoffset: 0.35; }
          100%     { stroke-dashoffset: 1; }
        }
        .scn-tile   { animation: scnTile 4.8s ease-out infinite; }
        .scn-tile-1 { animation-delay: 0.25s; }
        .scn-tile-2 { animation-delay: 0.5s; }
        /* dash 1, gap 1.5: at offset 1 the whole dash sits before the
           path's start, so the round caps draw no dot at the reset */
        .scn-line   { stroke-dasharray: 1 1.5; stroke-dashoffset: 1; animation: scnLine 4.8s ease-out infinite; }
        .scn-line-1 { animation-delay: 0.2s; }
        .scn-line-2 { animation-delay: 0.4s; }
        .scn-beam   { animation: scnBeam 4.8s cubic-bezier(.45,0,.55,1) infinite; }
        .scn-arc    { stroke-dasharray: 1 1.5; stroke-dashoffset: 1; animation: scnArc 4.8s ease-out infinite; }
        .scn-needle { transform-box: fill-box; transform-origin: 50% 100%; animation: scnNeedle 4.8s cubic-bezier(.34,1.2,.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .scn-tile, .scn-beam { animation: none; opacity: 1; transform: none; }
          .scn-beam { opacity: 0; }
          .scn-line, .scn-arc { animation: none; stroke-dashoffset: 0.35; }
          .scn-line { stroke-dashoffset: 0; }
          .scn-needle { animation: none; transform: rotate(25deg); }
        }
      `}</style>
    </svg>
  );
}

/* ── LostPinAnim - the 404 page ─────────────────────────────────────
   A map pin hops across a stylized block grid looking for its
   address; a "?" bubble pops where it lands. 6s loop. */
function LostPinAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg ref={sceneRef} viewBox="0 0 96 64" className="scene-svg h-full w-full" aria-hidden>
      {/* block grid - a stylized Baku quarter */}
      <g stroke={GRAY_L} strokeWidth="1.4" fill="none">
        <path d="M6 22 H90 M6 40 H90" />
        <path d="M26 8 V58 M50 8 V58 M72 8 V58" />
      </g>
      {/* two "known" buildings, quiet */}
      <rect x="32" y="27" width="12" height="8" rx="1.5" fill={GRAY_L} />
      <rect x="56" y="45" width="10" height="8" rx="1.5" fill={GRAY_L} />
      {/* the lost pin - hops between intersections */}
      <g className="lst-pin">
        <path
          d="M0 -14 C -4.4 -14 -8 -10.6 -8 -6.4 C -8 -2 -3 3.4 0 6 C 3 3.4 8 -2 8 -6.4 C 8 -10.6 4.4 -14 0 -14 Z"
          fill={INDIGO_D}
        />
        <circle cx="0" cy="-6.6" r="2.8" fill="#ffffff" />
      </g>
      {/* landing ring */}
      <circle className="lst-ring" cx="26" cy="40" r="4" fill="none" stroke={INDIGO} strokeWidth="1.4" />
      {/* the "?" bubble */}
      <g className="lst-q">
        <circle cx="66" cy="14" r="7" fill="#ffffff" stroke={AMBER} strokeWidth="1.5" />
        <text x="66" y="18" textAnchor="middle" fontSize="10" fontWeight="800" fill={AMBER}>?</text>
      </g>
      <style>{`
        @keyframes lstPin {
          0%, 8%   { transform: translate(26px, 40px); }
          18%      { transform: translate(38px, 26px) scale(1.04); }
          28%, 36% { transform: translate(50px, 40px); }
          46%      { transform: translate(62px, 30px) scale(1.04); }
          56%, 70% { transform: translate(72px, 22px); }
          86%, 100%{ transform: translate(26px, 40px); }
        }
        @keyframes lstRing {
          0%       { opacity: 0.9; transform: scale(0.4); }
          22%      { opacity: 0; transform: scale(1.6); }
          100%     { opacity: 0; transform: scale(1.6); }
        }
        @keyframes lstQ {
          0%, 56%  { opacity: 0; transform: scale(0.4); }
          64%      { opacity: 1; transform: scale(1.15); }
          70%, 84% { opacity: 1; transform: scale(1); }
          92%,100% { opacity: 0; transform: scale(0.8); }
        }
        .lst-pin  { animation: lstPin 6s cubic-bezier(.34,1.3,.64,1) infinite; }
        .lst-ring { transform-box: fill-box; transform-origin: center; animation: lstRing 6s ease-out infinite; }
        .lst-q    { transform-box: fill-box; transform-origin: center; animation: lstQ 6s cubic-bezier(.34,1.56,.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .lst-pin  { animation: none; transform: translate(48px, 32px); }
          .lst-ring { animation: none; opacity: 0; }
          .lst-q    { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}

/* ── TrialDimAnim - the trial-ended story ───────────────────────────
   Three Pro feature chips (alerts, seller history, photo intel) dim
   one after another - the lights going out - then the crown chip
   pulses: the way back. 7s loop. */
function TrialDimAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  const chip = (x: number, cls: string, children: React.ReactNode) => (
    <g className={cls}>
      <rect x={x} y="22" width="24" height="16" rx="8" fill={INDIGO} />
      {children}
    </g>
  );
  return (
    <svg ref={sceneRef} viewBox="0 0 120 64" className="scene-svg h-full w-full" aria-hidden>
      {chip(6, "trd-c trd-c0", (
        <path d="M14.5 33.5 h7 l-1.4 -2 v-3 a2.1 2.1 0 0 0 -4.2 0 v3 Z" fill="#fff" transform="translate(0,-1)" />
      ))}
      {chip(36, "trd-c trd-c1", (
        <path d="M42 34 l3 -4 l2.5 2 l3.5 -5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {chip(66, "trd-c trd-c2", (
        <g>
          <rect x="72" y="26.5" width="12" height="8" rx="1.6" fill="none" stroke="#fff" strokeWidth="1.6" />
          <circle cx="78" cy="30.5" r="2" fill="#fff" />
        </g>
      ))}
      {/* the crown - pulses after the lights go out */}
      <g className="trd-crown">
        <rect x="96" y="22" width="18" height="16" rx="8" fill={AMBER} />
        <path d="M101 33 l0 -5 l2.5 2 l1.5 -3.4 l1.5 3.4 l2.5 -2 l0 5 Z" fill="#fff" />
      </g>
      <style>{`
        @keyframes trdDim {
          0%, 18%   { opacity: 1; filter: none; }
          30%, 78%  { opacity: 0.28; }
          92%, 100% { opacity: 1; }
        }
        @keyframes trdCrown {
          0%, 40%  { transform: scale(1); }
          52%      { transform: scale(1.14); }
          60%      { transform: scale(1); }
          70%      { transform: scale(1.14); }
          78%,100% { transform: scale(1); }
        }
        .trd-c  { animation: trdDim 7s ease-out infinite; }
        .trd-c1 { animation-delay: 0.35s; }
        .trd-c2 { animation-delay: 0.7s; }
        .trd-crown { transform-box: fill-box; transform-origin: center; animation: trdCrown 7s cubic-bezier(.34,1.56,.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .trd-c { animation: none; opacity: 0.28; }
          .trd-crown { animation: none; }
        }
      `}</style>
    </svg>
  );
}

/* ── ShareFlowAnim - the install page's habit loop ──────────────────
   A phone frame: a listing card slides up, flies to the app squircle
   via the share arc, and the verdict chip pops out. 6.5s loop. */
function ShareFlowAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg ref={sceneRef} viewBox="0 0 96 88" className="scene-svg h-full w-full" aria-hidden>
      {/* phone frame */}
      <rect x="26" y="4" width="44" height="80" rx="9" fill="none" stroke={GRAY} strokeWidth="2" />
      <line x1="42" y1="10" x2="54" y2="10" stroke={GRAY} strokeWidth="2" strokeLinecap="round" />
      {/* the listing card inside */}
      <g className="shr-card">
        <rect x="32" y="20" width="32" height="24" rx="3" fill={INDIGO} fillOpacity="0.9" />
        <rect x="35" y="36" width="18" height="2.6" rx="1.3" fill="#fff" fillOpacity="0.85" />
        <rect x="35" y="23" width="26" height="10" rx="2" fill="#fff" fillOpacity="0.25" />
      </g>
      {/* share arc - a dot travels card -> app icon */}
      <path id="shrPath" d="M48 44 C 48 56 48 58 48 62" fill="none" stroke="none" />
      <circle className="shr-dot" cx="48" cy="44" r="2.6" fill={INDIGO_D} />
      {/* the app squircle at the bottom */}
      <g className="shr-app">
        <rect x="38" y="58" width="20" height="20" rx="6" fill={INDIGO_D} />
        <path d="M43 70 l5 -5 l5 5 v3 h-10 Z" fill="#fff" />
      </g>
      {/* verdict chip pops beside the phone */}
      <g className="shr-chip">
        <rect x="66" y="56" width="26" height="13" rx="6.5" fill={EMERALD} />
        <path d="M71 62.5 l2.4 2.4 l4.4 -5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="80" y="61" width="8" height="2.6" rx="1.3" fill="#fff" fillOpacity="0.9" />
      </g>
      <style>{`
        @keyframes shrCard {
          0%, 6%    { opacity: 0; transform: translateY(8px); }
          14%, 80%  { opacity: 1; transform: translateY(0); }
          90%, 100% { opacity: 0; transform: translateY(8px); }
        }
        @keyframes shrDot {
          0%, 22%  { opacity: 0; transform: translateY(0); }
          28%      { opacity: 1; }
          44%      { opacity: 1; transform: translateY(20px); }
          50%,100% { opacity: 0; transform: translateY(20px); }
        }
        @keyframes shrApp {
          0%, 40%  { transform: scale(1); }
          48%      { transform: scale(1.12); }
          54%, 100%{ transform: scale(1); }
        }
        @keyframes shrChip {
          0%, 50%   { opacity: 0; transform: scale(0.3); }
          58%       { opacity: 1; transform: scale(1.15); }
          64%, 82%  { opacity: 1; transform: scale(1); }
          92%, 100% { opacity: 0; transform: scale(0.8); }
        }
        .shr-card { animation: shrCard 6.5s cubic-bezier(.22,1,.36,1) infinite; }
        .shr-dot  { animation: shrDot 6.5s cubic-bezier(.45,0,.55,1) infinite; }
        .shr-app  { transform-box: fill-box; transform-origin: center; animation: shrApp 6.5s cubic-bezier(.34,1.56,.64,1) infinite; }
        .shr-chip { transform-box: fill-box; transform-origin: center; animation: shrChip 6.5s cubic-bezier(.34,1.56,.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .shr-card, .shr-app, .shr-chip { animation: none; opacity: 1; transform: none; }
          .shr-dot { animation: none; opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

/* ── PinDropAnim - the empty personal map ───────────────────────────
   A pin drops onto a mini map with a bounce and a settling ring -
   "your first save lands here". 5.5s loop. */
function PinDropAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg ref={sceneRef} viewBox="0 0 48 48" className="scene-svg h-full w-full" aria-hidden>
      <g stroke={GRAY_L} strokeWidth="1.2" fill="none">
        <path d="M4 30 H44 M16 12 V44 M32 12 V44" />
      </g>
      <circle className="pnd-ring" cx="24" cy="30" r="4" fill="none" stroke={INDIGO} strokeWidth="1.4" />
      <g className="pnd-pin">
        <path
          d="M24 14 C 20.2 14 17 17 17 20.8 C 17 24.8 21.4 29.4 24 31.8 C 26.6 29.4 31 24.8 31 20.8 C 31 17 27.8 14 24 14 Z"
          fill={INDIGO_D}
        />
        <circle cx="24" cy="20.6" r="2.5" fill="#ffffff" />
      </g>
      <style>{`
        @keyframes pndPin {
          0%, 8%   { opacity: 0; transform: translateY(-16px); }
          20%      { opacity: 1; transform: translateY(1.5px); }
          26%      { transform: translateY(-2px); }
          32%, 86% { transform: translateY(0); }
          94%,100% { opacity: 0; transform: translateY(0); }
        }
        @keyframes pndRing {
          0%, 18%  { opacity: 0; transform: scale(0.3); }
          24%      { opacity: 0.9; transform: scale(0.6); }
          46%      { opacity: 0; transform: scale(1.7); }
          100%     { opacity: 0; transform: scale(1.7); }
        }
        .pnd-pin  { animation: pndPin 5.5s cubic-bezier(.34,1.3,.64,1) infinite; }
        .pnd-ring { transform-box: fill-box; transform-origin: center; animation: pndRing 5.5s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pnd-pin { animation: none; opacity: 1; transform: none; }
          .pnd-ring { animation: none; opacity: 0; }
        }
      `}</style>
    </svg>
  );
}

/**
 * PhotoDropAnim - the listing wizard's empty photo zone. Two photo
 * cards drift up into a neat stack and a check blinks: "drop them
 * here, they land tidy". Loop leaves the stack resting most of the
 * cycle so it reads as a state, not a distraction.
 */
function PhotoDropAnimBase() {
  const sceneRef = useScenePause<SVGSVGElement>();
  return (
    <svg ref={sceneRef} viewBox="0 0 48 48" className="scene-svg h-full w-full" aria-hidden>
      {/* Resting frame card */}
      <rect x="12" y="14" width="24" height="19" rx="2.5" fill="none" stroke={GRAY_L} strokeWidth="1.4" />
      {/* Photo card 1 (back) */}
      <g className="phd-c1">
        <rect x="14" y="16" width="20" height="15" rx="2" fill="#ffffff" stroke={GRAY} strokeWidth="1.1" />
        <circle cx="19" cy="21" r="1.6" fill={AMBER} />
        <path d="M15.5 29 L21 23.5 L25 27 L28.5 24 L32.5 28" fill="none" stroke={GRAY} strokeWidth="1.1" />
      </g>
      {/* Photo card 2 (front, offset) */}
      <g className="phd-c2">
        <rect x="18" y="19" width="20" height="15" rx="2" fill="#ffffff" stroke={INDIGO} strokeWidth="1.2" />
        <circle cx="23" cy="24" r="1.6" fill={AMBER} />
        <path d="M19.5 32 L25 26.5 L29 30 L32.5 27 L36.5 31" fill="none" stroke={INDIGO} strokeWidth="1.2" />
      </g>
      {/* Landing tick */}
      <g className="phd-tick">
        <circle cx="37" cy="15" r="5" fill={EMERALD} />
        <path d="M34.6 15 L36.4 16.9 L39.6 13.4" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <style>{`
        @keyframes phdC1 {
          0%, 6%   { opacity: 0; transform: translate(-7px, 9px) rotate(-7deg); }
          22%      { opacity: 1; transform: translate(0, 0) rotate(0deg); }
          88%      { opacity: 1; transform: translate(0, 0); }
          96%,100% { opacity: 0; transform: translate(0, 0); }
        }
        @keyframes phdC2 {
          0%, 16%  { opacity: 0; transform: translate(8px, 10px) rotate(8deg); }
          34%      { opacity: 1; transform: translate(0, 0) rotate(0deg); }
          88%      { opacity: 1; transform: translate(0, 0); }
          96%,100% { opacity: 0; transform: translate(0, 0); }
        }
        @keyframes phdTick {
          0%, 38%  { opacity: 0; transform: scale(0.4); }
          48%      { opacity: 1; transform: scale(1.08); }
          54%      { transform: scale(1); }
          88%      { opacity: 1; }
          96%,100% { opacity: 0; }
        }
        .phd-c1, .phd-c2 { transform-box: fill-box; transform-origin: center; animation: phdC1 6s cubic-bezier(.22,1,.36,1) infinite; }
        .phd-c2 { animation-name: phdC2; }
        .phd-tick { transform-box: fill-box; transform-origin: center; animation: phdTick 6s ease-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .phd-c1, .phd-c2 { animation: none; opacity: 1; transform: none; }
          .phd-tick { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}

export const ScanAnim = memo(ScanAnimBase);
export const PhotoDropAnim = memo(PhotoDropAnimBase);
/** @public - waits for the 404 rewrite, an open item in the review checklist */
export const LostPinAnim = memo(LostPinAnimBase);
export const TrialDimAnim = memo(TrialDimAnimBase);
export const ShareFlowAnim = memo(ShareFlowAnimBase);
export const PinDropAnim = memo(PinDropAnimBase);

/* ── NotFoundScene - the 404 page (static) ──────────────────────────
   One strong object: a large solid indigo map pin with a "?" in its
   head, landed on a paper map with a soft street grid, two flat light
   indigo building blocks for scale. Surface on theme tokens (flips in
   dark mode), flat colour throughout: the indigo-to-violet gradient
   pin, the gradient blocks, the indigo glow under the pin and the amber
   sparkle were DESIGN.md's hard bans. No keyframes on purpose. */
function NotFoundSceneBase(props: SVGProps<SVGSVGElement>) {
  const sceneRef = useScenePause<SVGSVGElement>();
  const C_PAPER = "var(--color-paper-soft)";
  const C_RULE = "var(--color-rule)";
  return (
    <svg ref={sceneRef} viewBox="0 0 200 160" className="scene-svg h-full w-full" aria-hidden {...props}>
      {/* the map: a paper card with a quiet street grid */}
      <rect x="14" y="30" width="172" height="112" rx="16" fill={C_PAPER} stroke={C_RULE} strokeWidth="1.2" />
      <g stroke={C_RULE} strokeWidth="1.6" strokeLinecap="round">
        <path d="M14 74 H186 M14 108 H186" />
        <path d="M62 30 V142 M118 30 V142 M156 30 V142" />
      </g>
      {/* a park block and two buildings, so the grid reads as a city */}
      <rect x="126" y="80" width="24" height="22" rx="5" fill={EMERALD} fillOpacity="0.28" />
      <rect x="26" y="82" width="28" height="20" rx="4" fill={INDIGO_L} />
      <g fill="#ffffff" fillOpacity="0.85">
        <rect x="31" y="87" width="4" height="4" rx="1" /><rect x="38" y="87" width="4" height="4" rx="1" /><rect x="45" y="87" width="4" height="4" rx="1" />
        <rect x="31" y="94" width="4" height="4" rx="1" /><rect x="38" y="94" width="4" height="4" rx="1" /><rect x="45" y="94" width="4" height="4" rx="1" />
      </g>
      <rect x="164" y="40" width="16" height="28" rx="4" fill={INDIGO_L} />
      <g fill="#ffffff" fillOpacity="0.85">
        <rect x="168" y="45" width="3.5" height="3.5" rx="1" /><rect x="173.5" y="45" width="3.5" height="3.5" rx="1" />
        <rect x="168" y="52" width="3.5" height="3.5" rx="1" /><rect x="173.5" y="52" width="3.5" height="3.5" rx="1" />
        <rect x="168" y="59" width="3.5" height="3.5" rx="1" /><rect x="173.5" y="59" width="3.5" height="3.5" rx="1" />
      </g>

      {/* the pin's landing: a soft neutral shadow + ring at the intersection */}
      <ellipse cx="90" cy="109" rx="22" ry="5" fill={GRAY} fillOpacity="0.3" />
      <circle cx="90" cy="108" r="7" fill="none" stroke={INDIGO} strokeWidth="1.6" strokeOpacity="0.55" />

      {/* the pin - large, the one focal object. Teardrop: circle head
          (r 36, centre 54 above the tip) with tangent sides down to the
          tip at the intersection. */}
      <g transform="translate(90 108)">
        <path
          d="M0 0 C -6 -12 -36 -32 -36 -54 A 36 36 0 1 1 36 -54 C 36 -32 6 -12 0 0 Z"
          fill={INDIGO}
        />
        <circle cx="0" cy="-54" r="20" fill="#ffffff" />
        <text x="0" y="-44" textAnchor="middle" fontSize="30" fontWeight="800" fill={INDIGO_D}>?</text>
      </g>
    </svg>
  );
}
export const NotFoundScene = memo(NotFoundSceneBase);
